/**
 * <plakkie-customiser>
 *
 * Live preview for the Plakkie customiser section:
 * - Sole / strap swatches (radios) set --plakkie-sole / --plakkie-strap on the element, which the
 *   preview SVG reads for its .plakkie-part-* layers.
 * - A custom colour swatch pairs a radio with a native <input type="color">; picking a colour
 *   writes it into the radio (data-plakkie-color, value "Custom #HEX") and selects it.
 * - Branding chips (checkboxes) show the matching [data-plakkie-logo] placeholder in the SVG.
 * - "Quote this design" writes the summary into every [data-plakkie-design-field] (the quote form's
 *   hidden field) and fires a `plakkie:design` event, then the link scrolls to the form.
 */

const DESIGN_EVENT = 'plakkie:design';
const STORAGE_KEY = 'plakkie:design';

class PlakkieCustomiser extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#handleChange);
    this.addEventListener('input', this.#handlePicker);
    this.addEventListener('click', this.#handleClick);
    this.#ensureSelection('sole');
    this.#ensureSelection('strap');
    this.#render();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.removeEventListener('input', this.#handlePicker);
    this.removeEventListener('click', this.#handleClick);
  }

  /** @param {'sole' | 'strap'} part */
  #swatches(part) {
    return [...this.querySelectorAll(`input[data-plakkie-part="${part}"]`)];
  }

  /** @param {'sole' | 'strap'} part */
  #selected(part) {
    return this.#swatches(part).find((input) => input.checked);
  }

  #branding() {
    return [...this.querySelectorAll('input[data-plakkie-branding]:checked')];
  }

  /** Falls back to the first swatch when no default is ticked in the editor. */
  #ensureSelection(part) {
    const swatches = this.#swatches(part);
    if (swatches.length && !swatches.some((input) => input.checked)) swatches[0].checked = true;
  }

  #handleChange = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.matches('[data-plakkie-picker]')) {
      this.#handlePicker(event);
      return;
    }
    if (event.target instanceof HTMLInputElement && event.target.closest('[data-plakkie-group]')) {
      this.#render();
    }
  };

  /** Live update while the native colour picker is open. */
  #handlePicker = (event) => {
    const picker = event.target;
    if (!(picker instanceof HTMLInputElement) || !picker.matches('[data-plakkie-picker]')) return;

    const swatch = picker.closest('.plakkie-swatch');
    const radio = swatch?.querySelector('input[data-plakkie-custom]');
    const label = swatch?.querySelector('.plakkie-swatch__label');
    if (!(radio instanceof HTMLInputElement)) return;

    const hex = picker.value.toUpperCase();
    radio.dataset.plakkieColor = hex;
    radio.value = `${radio.dataset.plakkieCustomLabel || 'Custom'} ${hex}`;
    radio.checked = true;
    if (label instanceof HTMLElement) label.style.setProperty('--plakkie-swatch', hex);
    this.#render();
  };

  #handleClick = (event) => {
    // Pointer clicks on the custom swatch open the colour picker. Keyboard selection
    // (arrow keys also fire click, with detail 0) only selects it; the picker is the next tab stop.
    const custom = event.target instanceof Element && event.target.closest('.plakkie-swatch--custom');
    if (custom && event.detail > 0) {
      const picker = custom.querySelector('[data-plakkie-picker]');
      if (picker instanceof HTMLInputElement) {
        try {
          picker.showPicker();
        } catch {
          picker.click();
        }
      }
    }

    const button = event.target instanceof Element && event.target.closest('[data-plakkie-quote-design]');
    if (!button) return;

    const design = this.summary;
    for (const field of document.querySelectorAll('[data-plakkie-design-field]')) {
      field.value = design;
      field.dispatchEvent(new Event('change', { bubbles: true }));
    }

    try {
      sessionStorage.setItem(STORAGE_KEY, design);
    } catch {
      // Storage can be unavailable (private mode); the hidden field already holds the value.
    }

    document.dispatchEvent(new CustomEvent(DESIGN_EVENT, { detail: { design } }));
  };

  get summary() {
    const { soleSuffix = 'sole', strapSuffix = 'strap' } = this.dataset;
    const sole = this.#selected('sole');
    const strap = this.#selected('strap');
    const parts = [];

    if (sole) parts.push(`${sole.value} ${soleSuffix}`.trim());
    if (strap) parts.push(`${strap.value} ${strapSuffix}`.trim());
    for (const chip of this.#branding()) parts.push(chip.value);

    return parts.join(' · ');
  }

  #render() {
    const sole = this.#selected('sole');
    const strap = this.#selected('strap');

    if (sole?.dataset.plakkieColor) this.style.setProperty('--plakkie-sole', sole.dataset.plakkieColor);
    if (strap?.dataset.plakkieColor) this.style.setProperty('--plakkie-strap', strap.dataset.plakkieColor);

    for (const group of this.querySelectorAll('[data-plakkie-group]')) {
      const output = group.querySelector('[data-plakkie-group-value]');
      if (!output) continue;
      const checked = group.querySelector('input[type="radio"]:checked');
      output.textContent = checked instanceof HTMLInputElement ? checked.value : '';
    }

    const placements = new Set(this.#branding().map((chip) => chip.dataset.plakkiePlacement));
    for (const logo of this.querySelectorAll('[data-plakkie-logo]')) {
      logo.classList.toggle('is-active', placements.has(logo.getAttribute('data-plakkie-logo')));
    }

    const summaryOutput = this.querySelector('[data-plakkie-summary]');
    if (summaryOutput) summaryOutput.textContent = this.summary;
  }
}

if (!customElements.get('plakkie-customiser')) {
  customElements.define('plakkie-customiser', PlakkieCustomiser);
}
